/* eslint-disable @typescript-eslint/no-explicit-any */
// Lib
import React, { useRef, useState } from 'react';
import {
  GetStaticComponentProps,
  Item,
  useSitecoreContext,
  Text,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { tailwindVariants } from './IngredientAccordionTailwind';
import { sendGTMEvent } from '@next/third-parties/google';
import { ComponentProps } from 'lib/component-props';
import { useI18n } from 'next-localization';
import { stripHtml } from 'lib/utils/regex';

import Container from '../Layout/DartContainer/DartContainer';
import graphQLClientFactory from 'lib/graphql-client-factory';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';

export type AccordionListProps = ComponentProps & { staticProps?: Result };

export type Ingredient = {
  targetItem: {
    name: string;
    title: { value: string };
    definition: { value: string };
  };
};

export type Role = {
  targetItem: {
    name: string;
    title: { value: string };
    definition: { value: string };
  };
};

export type Language = { name: string };

export type ActiveCountry = {
  name: string;
  item: {
    languageList: { targetItems: Language[] };
  };
  id: string;
};

export type FormulaIngredient = {
  name: string;
  sortorder?: { value?: string | number };
  ingredient: Ingredient;
  role: Role;
  isFragranceTarget?: boolean;
  oil: { value: string };
  allergen: { value: string };
  activeIngredientCountryMapping: { targetItems: ActiveCountry[] };
};

export type Fragrance = {
  name: string;
  sortorder?: { value?: string | number };
  oil: { value: string };
  fragrances: {
    targetItems: {
      name: string;
      sortorder?: { value?: string | number };
      title: { value: string };
      allergen: { value: string };
    }[];
  };
};

type FragranceTargetItem = {
  name: string;
  sortorder?: { value?: string | number };
  title: { value: string };
  allergen: { value: string };
  isFragranceTarget?: boolean;
  oil: { value: string };
};

export type Result = {
  activeIngredients: FormulaIngredient[];
  otherIngredients: FormulaIngredient[];
  allIngredients?: (FormulaIngredient | FragranceTargetItem)[];
  isRegistered: boolean;
  fragrances: Fragrance[];
};

export type FormulaIngredients = {
  item: {
    children: { total: string; results: FormulaIngredient[] };
  };
};

export type FormulaFragrances = {
  item: {
    children: { total: string; results: Fragrance[] };
  };
};

const IngredientAccordion = (props: AccordionListProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const contentDetails = sitecoreContext?.contentDetails as any;

  const { staticProps } = props;
  const { isRegistered, allIngredients = [] } = staticProps || {};

  const accordionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const i18n = useI18n();

  const IngredientsHeadline = i18n.t('Ingredients') || 'Ingredients';
  const ActiveIngredientsAccordionTitle = i18n.t('ActiveIngredients') || 'Active Ingredients';
  const OtherIngredientsAccordionTitle = i18n.t('OtherIngredients') || 'Other Ingredients';
  const FragranceAccordionTitle = i18n.t('Fragrance') || 'Fragrance';
  const FragranceOilAccordionTitle = i18n.t('FragranceOil') || 'Fragrance Oil';

  const allergenContent = contentDetails?.allergenContent || '';
  const fragranceContent = contentDetails?.fragranceContent || '';
  const registeredProductContent = contentDetails?.registeredProductContent || '';

  const {
    componentHeaderContentWrapper,
    componentTitle,
    panel,
    panelBody,
    panelBodyWrapper,
    panelHeader,
    panelTitle,
    panelTitleButton,
    panelWrapperReg,
    panelTitleIcon,
    panelTitleText,
    panelRoleText,
    panelWrapper,
    ingredientWrapper,
    backgroundBG,
    ingredientTitle,
    ingredientRole,
    ingredientDescription,
    fragranceDescription,
    ingredientDisclaimer,
    registeredProductRTE,
  } = tailwindVariants({
    isRegistered: isRegistered as boolean,
  });

  const {
    expandedFirstAccordionOnPageLoad: expandedFirstAccordionOnPageLoadParam,
    openMultipleAccordionAtSameTime,
  } = props?.params ?? {};

  const expandedFirstAccordionOnPageLoad = expandedFirstAccordionOnPageLoadParam ? 0 : -1;
  const [activeIndexes, setActiveIndexes] = useState<number[]>([expandedFirstAccordionOnPageLoad]);

  const id = props?.params?.RenderingIdentifier;

  const handleAccordionClick = (index: number, name: string) => {
    setActiveIndexes((prevIndexes) => {
      let newIndexes: number[] = [];

      if (openMultipleAccordionAtSameTime) {
        if (prevIndexes.includes(index)) {
          newIndexes = prevIndexes.filter((i) => i !== index);
        } else {
          newIndexes = [...prevIndexes, index];
        }
      } else {
        newIndexes = prevIndexes.includes(index) ? [] : [index];
      }

      return newIndexes;
    });

    // Send GTM event (keeps original keys)
    if (activeIndexes.includes(index)) {
      sendGTMEvent({
        event: 'accordion',
        type: 'close',
        'gtm.element.dataset.gtmDatasourceId': id,
        'gtm.element.dataset.gtmComponentName': name,
      });
      setActiveIndexes((prev) => prev.filter((item) => item !== index));
    } else {
      sendGTMEvent({
        event: 'accordion',
        type: 'open',
        'gtm.element.dataset.gtmDatasourceId': id,
        'gtm.element.dataset.gtmComponentName': name,
      });
    }

    const currentAccordion = accordionRefs.current[index];
    if (currentAccordion) {
      setTimeout(() => {
        const elementTop = currentAccordion.getBoundingClientRect().top + window.scrollY;
        const jumplinks = document.querySelector('#jump-links') as HTMLElement | null;
        const header = document.querySelector('header') as HTMLElement | null;
        const offset = jumplinks?.offsetHeight || header?.offsetHeight || 0;
        const newPosition = elementTop - offset;
        window.scrollTo({ top: newPosition, behavior: 'smooth' });
      }, 300);
    }
  };
  return (
    <>
      <div
        className={backgroundBG()}
        data-component="authorable/ingredientaccordion"
        id={id ? id : undefined}
        tabIndex={-1}
      >
        <Container>
          <div data-component="authorable/ingredientaccordion" data-testid="accordion">
            <div className={panelWrapper()}>
              {/* Headline for Unregistered */}
              {!isRegistered && (
                <div className={panelHeader()}>
                  <div className={componentHeaderContentWrapper()}>
                    <div className={componentTitle()}>
                      <Text encode={false} field={{ value: IngredientsHeadline }} tag="h2" />
                    </div>
                  </div>
                </div>
              )}

              {isRegistered ? (
                <>
                  {/* Active Ingredients Accordion */}
                  <div
                    ref={(el) => {
                      if (el) accordionRefs.current[0] = el;
                    }}
                    className={panel()}
                  >
                    <button className={panelTitleButton()}>
                      <RichTextA11yWrapper field={{ value: ActiveIngredientsAccordionTitle }} />
                    </button>

                    <>
                      {allIngredients?.filter((item: FormulaIngredient) => {
                        const roleTitle = item?.role?.targetItem?.title?.value
                          ?.toLowerCase()
                          ?.trim();
                        return roleTitle === 'active ingredient' && !item.isFragranceTarget;
                      }).length > 0 && (
                        <ul className={panelBodyWrapper()}>
                          {allIngredients
                            ?.filter((item: FormulaIngredient) => {
                              const roleTitle = item?.role?.targetItem?.title?.value
                                ?.toLowerCase()
                                ?.trim();
                              return roleTitle === 'active ingredient' && !item.isFragranceTarget;
                            })
                            ?.map((item: FormulaIngredient, i: number) => (
                              <li className={panelBody()} key={i}>
                                <div className={ingredientWrapper()}>
                                  <div className={ingredientTitle()}>
                                    <Text
                                      tag="h3"
                                      field={{
                                        value: item?.ingredient?.targetItem?.title?.value,
                                      }}
                                    />
                                    {item?.allergen?.value === '1' && '*'}
                                  </div>

                                  <div className={ingredientRole()}>
                                    <Text
                                      field={{
                                        value: item?.role?.targetItem?.title?.value,
                                      }}
                                    />
                                  </div>

                                  <div className={ingredientDescription()}>
                                    <Text
                                      field={{
                                        value: item?.role?.targetItem?.definition?.value,
                                      }}
                                    />
                                  </div>

                                  {item?.allergen?.value === '1' && (
                                    <div className={ingredientDisclaimer()}>
                                      <RichTextA11yWrapper
                                        field={{
                                          value: allergenContent,
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                        </ul>
                      )}
                    </>
                  </div>

                  {/* Other Ingredients + Fragrances Accordion */}
                  <div
                    ref={(el) => {
                      accordionRefs.current[1] = el;
                    }}
                    className={panel()}
                  >
                    <button className={panelTitleButton()}>
                      <RichTextA11yWrapper field={{ value: OtherIngredientsAccordionTitle }} />
                    </button>

                    <ul className={panelBodyWrapper()}>
                      {(() => {
                        const renderedIndexes = new Set<number>();
                        const otherAndFragranceIngredients = allIngredients.filter(
                          (item: FormulaIngredient) => {
                            const roleTitle = item?.role?.targetItem?.title?.value
                              ?.toLowerCase()
                              ?.trim();
                            return roleTitle !== 'active ingredient';
                          }
                        );

                        return otherAndFragranceIngredients.map(
                          (item: FormulaIngredient, index: number) => {
                            if (renderedIndexes.has(index)) return null;

                            const isFragrance = item.isFragranceTarget;

                            if (isFragrance) {
                              const fragranceItems = otherAndFragranceIngredients.filter(
                                (f) => f.isFragranceTarget
                              );
                              fragranceItems.forEach((_, i) => renderedIndexes.add(index + i));

                              const hasAllergen = fragranceItems.some(
                                (f) => f?.allergen?.value === '1'
                              );
                              const hasOil = fragranceItems.some(
                                (f) => f?.oil?.value && f?.oil?.value.trim() !== ''
                              );

                              return (
                                <li className={panelBody()} key={`fragrance-${index}`}>
                                  <div className={ingredientWrapper()}>
                                    <div className={ingredientTitle()}>
                                      <Text
                                        tag="h3"
                                        field={{
                                          value: hasOil
                                            ? FragranceOilAccordionTitle
                                            : FragranceAccordionTitle,
                                        }}
                                      />
                                    </div>

                                    <div className={ingredientRole()}>
                                      <Text field={{ value: FragranceAccordionTitle }} />
                                    </div>

                                    <div className={fragranceDescription()}>
                                      {fragranceItems.map((f, fIndex) => {
                                        const isLast = fIndex === fragranceItems.length - 1;
                                        return (
                                          <span key={fIndex} className="pr-2">
                                            <Text
                                              field={{
                                                value: f.name,
                                              }}
                                            />
                                            {f?.allergen?.value === '1'
                                              ? '*; '
                                              : !isLast
                                              ? '; '
                                              : ''}
                                          </span>
                                        );
                                      })}
                                    </div>

                                    {hasAllergen && (
                                      <div className={ingredientDisclaimer()}>
                                        <RichTextA11yWrapper field={{ value: allergenContent }} />
                                      </div>
                                    )}

                                    <div className={ingredientDisclaimer()}>
                                      <RichTextA11yWrapper field={{ value: fragranceContent }} />
                                    </div>
                                  </div>
                                </li>
                              );
                            }

                            // Non-fragrance item
                            return (
                              <li className={panelBody()} key={index}>
                                <div className={ingredientWrapper()}>
                                  <div className={ingredientTitle()}>
                                    <Text
                                      tag="h3"
                                      field={{
                                        value: item?.ingredient?.targetItem?.title?.value,
                                      }}
                                    />
                                    {item?.allergen?.value === '1' && '*'}
                                  </div>
                                  <div className={ingredientRole()}>
                                    <Text
                                      field={{
                                        value: item?.role?.targetItem?.title?.value,
                                      }}
                                    />
                                  </div>
                                  <div className={ingredientDescription()}>
                                    <Text
                                      field={{
                                        value: item?.role?.targetItem?.definition?.value,
                                      }}
                                    />
                                  </div>
                                </div>
                                {item?.allergen?.value === '1' && (
                                  <div className={ingredientDisclaimer()}>
                                    <RichTextA11yWrapper
                                      field={{
                                        value: allergenContent,
                                      }}
                                    />
                                  </div>
                                )}
                              </li>
                            );
                          }
                        );
                      })()}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  {/* Unregistered Accordion */}
                  {(() => {
                    let fragranceAccordionRendered = false;
                    const fragranceItems =
                      allIngredients?.filter((i: any) => i.isFragranceTarget) || [];

                    return allIngredients?.map((item: any, index: number) => {
                      const isFragrance = item?.isFragranceTarget;
                      const { panelBodyWrapper } = tailwindVariants({
                        isOpen: activeIndexes.includes(index),
                        isRegistered,
                        isFragrance,
                      });

                      // Skip fragrance items if we're going to render them together
                      if (isFragrance) {
                        if (fragranceAccordionRendered || fragranceItems.length === 0) return null;

                        fragranceAccordionRendered = true;
                        const hasAllergen = fragranceItems.some((f) => f.allergen?.value === '1');
                        const hasOil = fragranceItems.some(
                          (f) => f?.oil?.value && f?.oil?.value.trim() !== ''
                        );
                        return (
                          <div
                            key="fragrance-accordion"
                            ref={(el) => {
                              accordionRefs.current[index] = el;
                            }}
                            className={panel()}
                          >
                            <div className={panelTitle()}>
                              <button
                                className={panelTitleButton()}
                                onClick={() =>
                                  handleAccordionClick(index, openMultipleAccordionAtSameTime)
                                }
                                aria-expanded={activeIndexes.includes(index)}
                              >
                                <div className={panelWrapperReg()}>
                                  <div className={panelTitleText()}>
                                    <RichTextA11yWrapper
                                      field={{
                                        value: hasOil
                                          ? FragranceOilAccordionTitle
                                          : FragranceAccordionTitle,
                                      }}
                                    />
                                  </div>
                                  <div className={panelRoleText()}>
                                    <Text field={{ value: FragranceAccordionTitle }} />
                                  </div>
                                </div>
                                <GoogleMaterialSymbol
                                  className={panelTitleIcon()}
                                  icon={
                                    activeIndexes.includes(index) ? 'expand_less' : 'expand_more'
                                  }
                                />
                              </button>
                            </div>

                            <div
                              className={panelBodyWrapper()}
                              aria-expanded={activeIndexes.includes(index)}
                            >
                              <div className={fragranceDescription()}>
                                {fragranceItems.map((f, i) => {
                                  const isLast = i === fragranceItems.length - 1;
                                  return (
                                    <span key={i} className="pr-2">
                                      <Text field={{ value: f.name }} />
                                      {f.allergen?.value === '1' ? '*; ' : !isLast ? '; ' : ''}
                                    </span>
                                  );
                                })}
                              </div>

                              {hasAllergen && (
                                <div className={ingredientDisclaimer()}>
                                  <RichTextA11yWrapper field={{ value: allergenContent }} />
                                </div>
                              )}
                              <div className={ingredientDisclaimer()}>
                                <RichTextA11yWrapper field={{ value: fragranceContent }} />
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Normal ingredient rendering (non-fragrance)
                      return (
                        <div
                          key={index}
                          ref={(el) => {
                            accordionRefs.current[index] = el;
                          }}
                          className={panel()}
                        >
                          <div className={panelTitle()}>
                            <button
                              aria-label={stripHtml(
                                item?.ingredient?.targetItem?.title?.value || ''
                              )}
                              type="button"
                              className={panelTitleButton()}
                              onClick={() =>
                                handleAccordionClick(index, openMultipleAccordionAtSameTime)
                              }
                              aria-expanded={activeIndexes.includes(index)}
                            >
                              <div className={panelWrapperReg()}>
                                <div className={panelTitleText()}>
                                  <RichTextA11yWrapper
                                    field={{ value: item?.ingredient?.targetItem?.title?.value }}
                                  />
                                  {item?.allergen?.value === '1' && '*'}
                                </div>
                                <div className={panelRoleText()}>
                                  <RichTextA11yWrapper
                                    field={{ value: item?.role?.targetItem?.title?.value }}
                                  />
                                </div>
                              </div>
                              <GoogleMaterialSymbol
                                className={panelTitleIcon()}
                                icon={activeIndexes.includes(index) ? 'expand_less' : 'expand_more'}
                              />
                            </button>
                          </div>

                          <div
                            className={panelBodyWrapper()}
                            aria-expanded={activeIndexes.includes(index)}
                          >
                            <RichTextA11yWrapper
                              field={{ value: item?.ingredient?.targetItem?.definition?.value }}
                            />
                            {item?.allergen?.value === '1' && (
                              <div className={ingredientDisclaimer()}>
                                <RichTextA11yWrapper field={{ value: allergenContent }} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>
              )}
            </div>
          </div>
        </Container>
      </div>

      {isRegistered && (
        <div className={registeredProductRTE()}>
          <RichTextA11yWrapper field={{ value: registeredProductContent }} />
        </div>
      )}
    </>
  );
};

export default IngredientAccordion;

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  const graphQLClient = graphQLClientFactory({});
  const site = layoutData?.sitecore?.context?.site?.name;
  if (site?.toLocaleLowerCase() !== 'wiscj') {
    return {
      staticProps: null,
    };
  }

  const formula =
    layoutData?.sitecore?.route?.fields?.productInformation &&
    'fields' in layoutData?.sitecore?.route?.fields?.productInformation
      ? (layoutData?.sitecore?.route?.fields?.productInformation?.fields?.formula as Item[])
      : null;
  const formulaId = formula && formula.length > 0 ? formula[0]?.id : '';
  const language = layoutData?.sitecore?.context?.language;

  // Fetch ingredients
  const data = await graphQLClient.request<FormulaIngredients>(query, {
    path: formulaId,
    language: language,
  });

  const formulaIngredients = data && data?.item?.children?.results;
  let isActive = false;
  const result: Result = {
    activeIngredients: [],
    otherIngredients: [],
    isRegistered: false,
    fragrances: [],
  };

  if (formulaIngredients && formulaIngredients.length > 0) {
    for (const formulaIngredient of formulaIngredients) {
      const activeCountries = formulaIngredient?.activeIngredientCountryMapping?.targetItems;
      if (activeCountries && activeCountries.length > 0) {
        for (const country of activeCountries) {
          const countryData = await graphQLClient.request<ActiveCountry>(countryQuery, {
            path: country?.id,
            language: 'en',
          });
          const languageItems = Array.isArray(countryData?.item?.languageList?.targetItems)
            ? countryData.item.languageList.targetItems
            : [];

          isActive = false;
          for (const lang of languageItems) {
            if (language?.toLocaleLowerCase() === lang?.name?.toString().toLocaleLowerCase()) {
              isActive = true;
              break;
            }
          }
          if (isActive) {
            break;
          }
        }
        if (isActive) {
          result.isRegistered = true;
          result.activeIngredients.push(formulaIngredient);
        } else {
          result.otherIngredients.push(formulaIngredient);
        }
      } else {
        result.otherIngredients.push(formulaIngredient);
      }
    }
  }

  // Fetch fragrance lists
  const fragranceData = await graphQLClient.request<FormulaFragrances>(fragranceQuery, {
    path: formulaId,
    language: language,
  });

  const formulaFragrances = fragranceData?.item?.children?.results ?? [];

  result.fragrances.push(...formulaFragrances);

  // Sort each group by sitecore sortorder
  if (result.activeIngredients?.length) {
    result.activeIngredients.sort(
      (a, b) => Number(a.sortorder?.value ?? 0) - Number(b.sortorder?.value ?? 0)
    );
  }

  if (result.otherIngredients?.length) {
    result.otherIngredients.sort(
      (a, b) => Number(a.sortorder?.value ?? 0) - Number(b.sortorder?.value ?? 0)
    );
  }

  if (result.fragrances?.length) {
    result.fragrances.sort(
      (a, b) => Number(a.sortorder?.value ?? 0) - Number(b.sortorder?.value ?? 0)
    );
  }

  // Step: build fragrance target items (mark as fragrance targets)
  const fragranceTargets =
    result.fragrances?.[0]?.fragrances?.targetItems?.map((t: any) => ({
      ...t,
      isFragranceTarget: true,
      oil: result.fragrances?.[0]?.oil,
    })) || [];

  // Get numeric sort order helper: fragrance targets follow fragrance parent's sortorder
  const fragranceSortOrder = Number(result.fragrances?.[0]?.sortorder?.value ?? 0);
  const getSortOrder = (item: any) => {
    if (item?.isFragranceTarget) return fragranceSortOrder;
    return Number(item?.sortorder?.value ?? 0) || 0;
  };

  // Merge final list according to registration state
  if (result.isRegistered) {
    // Registered: Active first, then otherIngredients + fragranceTargets ordered by Sitecore sort order (as per unregistered)
    const nonActiveCombined = [...(result.otherIngredients || []), ...fragranceTargets].sort(
      (a, b) => getSortOrder(a) - getSortOrder(b)
    );

    result.allIngredients = [...(result.activeIngredients || []), ...nonActiveCombined];
  } else {
    // Unregistered: combine everything and sort globally (Sitecore order; fragrance targets take fragrance sort order)
    const combined = [
      ...(result.activeIngredients || []),
      ...(result.otherIngredients || []),
      ...fragranceTargets,
    ];

    result.allIngredients = combined.sort((a, b) => getSortOrder(a) - getSortOrder(b));
  }
  return {
    staticProps: result,
  };
};

const query = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children(includeTemplateIDs: ["{89BC1BBC-B4D7-4580-97DF-2B20E23E0124}"], first: 200) {
        total
        results {
          ... on FormulaIngredient {
            name
            sortorder: field(name: "__sortorder") {
              value
            }
            allergen: field(name: "allergen") {
              value
            }
            ingredient: field(name: "ingredient") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                  definition: field(name: "definition") {
                    value
                  }
                }
              }
            }
            role: field(name: "role") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                  definition: field(name: "definition") {
                    value
                  }
                }
              }
            }
            activeIngredientCountryMapping: field(name: "activeIngredientCountryMapping") {
              ... on MultilistField {
                targetItems {
                  ... on CountryName {
                    name
                    id
                    title: field(name: "title") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const fragranceQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children(includeTemplateIDs: ["{9B396867-3CBF-4575-9917-0D674E3E8F33}"], first: 200) {
        total
        results {
          ... on FragranceList {
            name
            sortorder: field(name: "__sortorder") {
              value
            }
            oil: field(name: "oil") {
              value
            }
            fragrances: field(name: "fragrances") {
              ... on MultilistField {
                targetItems {
                  ... on Fragrance {
                    name
                    sortorder: field(name: "__sortorder") {
                      value
                    }
                    title: field(name: "title") {
                      value
                    }
                    allergen: field(name: "allergen") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const countryQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      languageList: field(name: "languageList") {
        ... on MultilistField {
          targetItems {
            id
            name
          }
        }
      }
    }
  }
`;
